import { isPlatformServer } from '@angular/common';
import { DOCUMENT, DestroyRef, Injectable, PLATFORM_ID, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { combineLatest } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import { MarketingConsentCategory } from './one-trust.service';
import { SettingsService } from './settings.service';
import { UserConsentService } from './user-consent.service';

type LinkedInTrackingRequest = [action: string, data?: unknown];

interface LinkedInTracker {
  (action: string, data?: unknown): void;
  q: LinkedInTrackingRequest[];
}

declare const window: {
  _linkedin_partner_id?: string;
  _linkedin_data_partner_ids?: string[];
  lintrk?: LinkedInTracker;
}

@Injectable({
  providedIn: 'root',
})
export class LinkedInInsightTagService {

  private document = inject(DOCUMENT);
  private platformId = inject(PLATFORM_ID);
  private settingsService = inject(SettingsService);
  private userConsentService = inject(UserConsentService);
  private destroyRef = inject(DestroyRef);

  private marketingConsent = this.userConsentService.forCategory(MarketingConsentCategory);

  public init(): void {
    if (isPlatformServer(this.platformId)) {
      return;
    }

    combineLatest([
      this.settingsService.linkedInInsightTagPartnerId,
      this.marketingConsent.given$.pipe(filter(given => given === true)),
    ]).pipe(
      map(([partnerId]) => partnerId?.trim()),
      filter((partnerId): partnerId is string => !!partnerId),
      take(1),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((partnerId) => {
      this.loadInsightTag(partnerId);
    });
  }

  private loadInsightTag(partnerId: string): void {
    window._linkedin_partner_id = partnerId;
    window._linkedin_data_partner_ids = window._linkedin_data_partner_ids ?? [];
    if (!window._linkedin_data_partner_ids.includes(partnerId)) {
      window._linkedin_data_partner_ids.push(partnerId);
    }

    if (!window.lintrk) {
      const linkedInTracker = ((action: string, data?: unknown) => {
        linkedInTracker.q.push([action, data]);
      }) as LinkedInTracker;
      linkedInTracker.q = [];
      window.lintrk = linkedInTracker;
    }

    if (this.document.getElementById('linkedin-insight-tag') !== null) {
      return;
    }

    const scriptElement = this.document.createElement('script');
    scriptElement.id = 'linkedin-insight-tag';
    scriptElement.type = 'text/javascript';
    scriptElement.async = true;
    scriptElement.src = 'https://snap.licdn.com/li.lms-analytics/insight.min.js';

    this.document.body.appendChild(scriptElement);
  }

}
